import translate from '@allesauto/i18n';
import { useWindowSize } from '@allesauto/utils/hooks';
import { sbEditable } from '@storyblok/storyblok-editable';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import FilterCarBrandsList from './FilterCarBrandsList';
import {
  BackgroundColors,
  DisplayValueTypes,
  FilterCarBrandsItem,
  FilterCarBrandsProps,
} from './types';

const CLASS_NAMES = {
  [BackgroundColors.WHITE]: {
    container: 'flex flex-col bg-white px-40px py-60px',
    heading:
      'break-words font-headline font-weight-bold line-clamp text-ellipsis text-headline3 sm:mb-40px md:mb-10px text-black',
    description: 'pb-20px lg:w-[50%] sm:w-[100%] text-100 text-grey80',
    itemContainer: 'gap-15px grid sm:grid-cols-2 lg:grid-cols-5',
    viewAll:
      'flex items-center self-center rounded-default mt-40px bg-purple80 px-40px py-15px text-white font-weight-bold',
  },
  [BackgroundColors.GREY]: {
    container: 'flex flex-col bg-grey10 px-40px py-60px',
    heading:
      'break-words font-headline font-weight-bold line-clamp text-ellipsis text-headline3 sm:mb-40px md:mb-10px text-black',
    description: 'pb-20px w-full text-grey80',
    itemContainer: 'gap-15px grid sm:grid-cols-2 lg:grid-cols-5',
    viewAll:
      'flex items-center self-center rounded-default mt-40px bg-purple80 px-40px py-15px text-white font-weight-bold',
  },
};

const DEFAULT_MAX_NUMBER_OF_ITEMS = 10;

export type { FilterCarBrandsItem as FilterCarBrandsItems };

export default function FilterCarBrands(props: FilterCarBrandsProps) {
  const {
    items,
    background,
    heading,
    description,
    displayValue,
    filterCarBrandsItems,
    priceFontColor,
    vehiclesAmountFontColor,
    displayShowMoreButton = true,
  } = props;
  const [maxNumberOfItems, setMaxNumberOfItems] = useState(items.length);
  const [isViewMore, setIsViewMore] = useState(true);

  const intl = useIntl();
  const [width] = useWindowSize();

  useEffect(() => {
    const maxNumberOfItems = DEFAULT_MAX_NUMBER_OF_ITEMS;
    setIsViewMore(true);
    setMaxNumberOfItems(maxNumberOfItems);
  }, [items.length, width]);

  const handleViewMode = () => {
    const maxNumberOfItems = DEFAULT_MAX_NUMBER_OF_ITEMS;
    isViewMore
      ? setMaxNumberOfItems(items.length)
      : setMaxNumberOfItems(maxNumberOfItems);
    setIsViewMore(!isViewMore);
  };

  const getItem = (id: string) => {
    const item = filterCarBrandsItems.find((value) => value._uid === id);
    return item;
  };

  const getPrice = (id?: string) => {
    if (!id) return 0;

    const item = getItem(id);

    return item?.price ?? 0;
  };

  const getOffersAmount = (id?: string) => {
    if (!id) return 0;
    const item = getItem(id);

    return item?.offersAmount ?? 0;
  };

  return (
    <div {...sbEditable(props)} className={CLASS_NAMES[background].container}>
      <div className={CLASS_NAMES[background].heading}>{heading}</div>
      <div className={CLASS_NAMES[background].description}>{description}</div>
      <div className={CLASS_NAMES[background].itemContainer}>
        {items.slice(0, maxNumberOfItems).map((item) => (
          <FilterCarBrandsList
            key={item._uid}
            {...item}
            background={background}
            displayValue={displayValue}
            offersAmount={getOffersAmount(item._uid)}
            price={getPrice(item._uid)}
            priceFontColor={priceFontColor}
            vehiclesAmountFontColor={vehiclesAmountFontColor}
          />
        ))}
      </div>
      {items.length > 10}
      {displayShowMoreButton && (
        <button
          className={CLASS_NAMES[background].viewAll}
          onClick={handleViewMode}
        >
          {isViewMore && translate(intl, 'ui.FILTER_PAGE.SHOW_ALL')}
          {!isViewMore && translate(intl, 'ui.FILTER_PAGE.SHOW_LESS')}
        </button>
      )}
    </div>
  );
}
